import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { toast } from "react-toastify";

export default function RentalDashboard() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("reservations");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  
  const now = new Date();
  const aktiiviset = reservations.filter((r) => new Date(r.end) >= now);
  const menneet = reservations.filter((r) => new Date(r.end) < now);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const profileType = localStorage.getItem("profileType");
    if (!token) return navigate("/kirjaudu");
    if (profileType !== "rental") return navigate("/choose-profile");
    try {
      const { user_id: userId } = jwtDecode(token);
      if (!userId) throw new Error("User ID puuttuu tokenista");
      fetchReservations(userId);
      fetchUserData(userId);
    } catch {
      navigate("/kirjaudu");
    }
  }, [navigate]);

  const fetchReservations = async (userId) => {
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/get_user_reservations.php",
        { userId }
      );
      if (data.success) setReservations(data.reservations);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (userId) => {
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/get_rental_user.php",
        { userId }
      );
      if (data.success) setUserData(data.user);
    } catch (e) {
      console.error(e);
    }
  };

  const formatDateFI = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("fi-FI", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSaveUserData = async () => {
    if (!userData) return;
    try {
      const payload = {
        userId: userData.id,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        postal_code: userData.postal_code,
        city: userData.city,
      };
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/update_rental_user.php",
        payload
      );
      data.success
        ? toast.success(data.message)
        : toast.error(data.error || "Tallennus epäonnistui");
    } catch (e) {
      console.error(e);
      toast.error("Palvelinvirhe tallennettaessa.");
    }
  };

  const handleDeleteAccount = async () => {
    if (!userData) return;
    const confirmDelete = window.confirm(
      "Haluatko varmasti poistaa tilisi?\n\n" +
        "- Kaikki tietosi poistetaan pysyvästi.\n" +
        "- Myös kaikki varauksesi poistuvat.\n" +
        "- Poistoa ei voi peruuttaa.\n\n" +
        "Vahvista painamalla OK."
    );
    if (!confirmDelete) return;
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/delete_rental_user.php",
        { userId: userData.id }
      );
      if (data.success) {
        toast.success("Tilisi ja varauksesi on poistettu.");
        handleLogout();
      } else {
        toast.error(data.error || "Tilin poistaminen epäonnistui.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Palvelinvirhe tilin poistossa.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("profileType");
    navigate("/kirjaudu");
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error("Täytä molemmat kentät.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Uuden salasanan tulee olla vähintään 8 merkkiä.");
      return;
    }
  
    try {
      const { data } = await axios.post(
        "https://spiderlegsproductions.com/api/change_password.php",
        {
          userId: userData.id,
          currentPassword,
          newPassword,
        }
      );
      if (data.success) {
        toast.success(data.message);
        setCurrentPassword("");
        setNewPassword("");
      } else {
        toast.error(data.error || "Virhe salasanan vaihdossa.");
      }
    } catch (e) {
      toast.error("Palvelinvirhe salasanan vaihdossa.");
      console.error(e);
    }
  };
  

  return (
    <div
      className="
        pt-24 pb-12 px-4 min-h-screen flex
        xxxsm:flex-col xxsm:flex-col s24:flex-col xsm:flex-col sm:flex-col
        md:flex-col mdd:flex-col lg:flex-row xl:flex-row 2xl:flex-row
        bg-linkcol dark:bg-violet dark:bg-opacity-75
        font-customFont1 text-darkest dark:text-primary dark:text-shadow-lg
      "
    >
      {/* SIDEBAR */}
      <aside
        className="
          mb-8
          xxxsm:w-full xxsm:w-full s24:w-full xsm:w-full sm:w-full
          md:w-full mdd:w-1/3 lg:w-1/4 xl:w-1/4 2xl:w-1/4
          bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md p-4
        "
      >
        <h2 className="text-2xl font-bold mb-6">Valikko</h2>
        <nav className="flex flex-col space-y-4">
          <button
            onClick={() => setActiveTab("reservations")}
            className={`text-left ${
              activeTab === "reservations" ? "font-bold underline" : ""
            }`}
          >
            Vuokraukset
          </button>
          <button
            onClick={() => setActiveTab("profile")}
            className={`text-left ${
              activeTab === "profile" ? "font-bold underline" : ""
            }`}
          >
            Henkilötiedot
          </button>
          <button
            onClick={() => setActiveTab("account")}
            className={`text-left ${
              activeTab === "account" ? "font-bold underline" : ""
            }`}
          >
            Tilin asetukset
          </button>
        </nav>
        <div className="mt-10">
          <ResponsiveButton onClick={handleLogout} variant="removefromcart">
            Kirjaudu ulos
          </ResponsiveButton>
        </div>
      </aside>

      {/* PÄÄSISÄLTÖ */}
      <main className="flex-1 p-4">
      {activeTab === "reservations" && (
  <>
    <h1 className="text-4xl font-bold mb-6">
      Tervetuloa{userData ? `, ${userData.name}` : ""}!
    </h1>
    {loading ? (
      <p>Ladataan varauksiasi...</p>
    ) : reservations.length ? (
      <>
        <h2 className="text-2xl font-semibold mt-6 mb-2">Aktiiviset varaukset</h2>
        <ul className="space-y-4 mb-6">
          {aktiiviset.map((r) => (
            <li key={r.varausID} className="p-4 bg-white dark:bg-darkest rounded-xl shadow-md">
              <h3 className="font-bold">
                {formatDateFI(r.start)} – {formatDateFI(r.end)}
              </h3>
              <p><strong>Status:</strong>{" "}
                {r.status === "vahvistettu" ? (
                  <span className="text-green-600 font-semibold">✅ Vahvistettu</span>
                ) : (
                  <span className="text-yellow-600 font-semibold">⏳ Odottaa vahvistusta</span>
                )}
              </p>
              <p>Nimi: {r.name}</p>
              <p>Sähköposti: {r.sahkoposti}</p>
              {r.products?.length > 0 && (
                <ul className="list-disc list-inside mt-2">
                  {r.products.map((p, i) => (
                    <li key={i}>
                      {p.tuote_nimi} ({p.quantity} kpl)
                      {p.unit_price && (
                        <> – {(p.quantity * p.unit_price).toFixed(2)} €</>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {r.total_price && (
                <p className="mt-2 font-semibold">
                  Yhteensä: {r.total_price.toFixed(2)} € (sis. alv)
                </p>
              )}
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mt-10 mb-2">Menneet varaukset</h2>
        {menneet.length ? (
          <ul className="space-y-4">
            {menneet.map((r) => (
              <li key={r.varausID} className="p-4 bg-gray-200 dark:bg-gray-800 rounded-xl shadow-sm opacity-70">
                <h3 className="font-bold">
                  {formatDateFI(r.start)} – {formatDateFI(r.end)}
                </h3>
                <p>Nimi: {r.name}</p>
                <p>Sähköposti: {r.sahkoposti}</p>
                {r.products?.length > 0 && (
                  <ul className="list-disc list-inside mt-2">
                    {r.products.map((p, i) => (
                      <li key={i}>
                        {p.tuote_nimi} ({p.quantity} kpl)
                        {p.unit_price && (
                          <> – {(p.quantity * p.unit_price).toFixed(2)} €</>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {r.total_price && (
                  <p className="mt-2 font-semibold">
                    Yhteensä: {r.total_price.toFixed(2)} € (sis. alv)
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>Ei menneitä varauksia.</p>
        )}
      </>
    ) : (
      <p>Sinulla ei ole vielä varauksia.</p>
    )}
  </>
)}


        {activeTab === "profile" && (
          <>
            <h1 className="text-4xl font-bold mb-6">Henkilötiedot</h1>
            {userData ? (
              <div className="bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md space-y-4 p-4">
                {[
                  { id: "name", label: "Nimi", type: "text", field: "name" },
                  { id: "email", label: "Sähköposti", type: "email", field: "email" },
                  { id: "phone", label: "Puhelin", type: "text", field: "phone" },
                  { id: "address", label: "Osoite", type: "text", field: "address" },
                  { id: "postal", label: "Postinumero", type: "text", field: "postal_code" },
                  { id: "city", label: "Kaupunki", type: "text", field: "city" },
                ].map(({ id, label, type, field }) => (
                  <div key={id}>
                    <label htmlFor={id} className="block mb-1 font-semibold">
                      {label}:
                    </label>
                    <input
                      id={id}
                      type={type}
                      value={userData[field] || ""}
                      onChange={(e) =>
                        setUserData({ ...userData, [field]: e.target.value })
                      }
                      className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
                      pattern={field === "postal_code" ? "^[0-9]{5}$" : undefined}
                      title={
                        field === "postal_code" ? "Viisi numeroa" : undefined
                      }
                    />
                  </div>
                ))}
                <div className="pt-4">
                  <ResponsiveButton onClick={handleSaveUserData} variant="addtocart">
                    Tallenna tiedot
                  </ResponsiveButton>
                </div>
              </div>
            ) : (
              <p>Ladataan tietojasi...</p>
            )}
          </>
        )}

{activeTab === "account" && (
  <>
    <h1 className="text-4xl font-bold mb-6">Tilin asetukset</h1>

    <div className="bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md p-4">
      <p className="font-semibold mb-2">Sähköposti: {userData?.email}</p>
      <p className="font-semibold mb-2">
        Tili luotu:{" "}
        {userData?.created_at
          ? new Date(userData.created_at).toLocaleDateString("fi-FI")
          : "-"}
      </p>

      <div className="p-4 bg-red-700 dark:bg-opacity-75 text-white rounded-lg mb-4">
        <p className="font-bold">Tiedoksi!</p>
        <p>
          Tilin poistaminen on pysyvää. Kaikki tietosi ja varauksesi
          poistuvat.
        </p>
      </div>

      <ResponsiveButton onClick={handleDeleteAccount} variant="removefromcart">
        Poista tili pysyvästi
      </ResponsiveButton>
    </div>

    {/* Vaihda salasana -osio */}
    {userData && (
      <div className="mt-10 p-6 space-y-4 bg-primary bg-opacity-75 dark:bg-violet dark:bg-opacity-75 rounded-3xl shadow-md">
        <h2 className="font-customFont1 dark:text-primary text-darkest dark:text-shadow-lg text-xl font-bold">Vaihda salasana</h2>

        <input
          type="password"
          placeholder="Nykyinen salasana"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
        />

        <input
          type="password"
          placeholder="Uusi salasana (väh. 8 merkkiä)"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full p-3 rounded-full bg-white dark:bg-darkest border-none focus:outline-none text-lg font-customFont1 pl-5"
        />

        <ResponsiveButton onClick={handleChangePassword}
        variant="addtocart">
          Vaihda salasana
        </ResponsiveButton>
      </div>
    )}
  </>
)}

        
      </main>
    </div>
  );
}
